// M005-F0055 (M005-D005 §8) — Bookings Hub Calendar model.
//
// Pure, unit-testable heart of the Calendar tab: turns the SAME
// `GET /api/bookings?org=` rows the Studio tab reads (§8: "no new Core
// route") into
//   - week-view data: one lane per room, each visible-week occurrence
//     placed by its real start/end time (WEB-BKHUB-008);
//   - month-view data: per-day density/status rollup cells (WEB-BKHUB-009).
//
// Series expansion is NOT reimplemented here: every rule-bearing series
// goes through the hub's own buildSeriesEntry (bookingsGrouping.js, the
// M009-F014 code path), which uses expandOccurrences (bounded by the
// rule's own COUNT/UNTIL or the 104-occurrence safety cap) and overlays
// materialised per-date exception rows onto the one date they belong to.
//
// The one genuinely calendar-specific problem is that a WEEKLY open-ended
// series anchored years ago expands (cap-bounded) entirely into the past,
// yet its next occurrence still belongs on the visible week. Moving the
// VIEWPORT is a model concern, not a parser concern: for open-ended (or
// UNTIL-only) WEEKLY series whose expansion ends before the requested
// range, the anchor is re-phased a whole number of interval-weeks forward
// — preserving weekday, wall-clock time and INTERVAL phase — and the SAME
// buildSeriesEntry (and therefore the same exception overlay) runs on the
// re-phased rows. COUNT-bounded series are never re-phased: their
// occurrence count is anchored to DTSTART, so a range past the expanded
// (cap-bounded) window is genuinely empty, exactly as the cards report it.
import {
  buildSeriesEntry,
  buildSeriesKey,
} from './bookingsGrouping';
import { getStatusBadgeClass, getStatusText, resolveDisplayStatus } from './bookingStatus';
import { describeSeriesPattern, expandOccurrences } from '../../utils/recurrenceParser';

const DAY_MS = 24 * 60 * 60 * 1000;

export const WEEKDAY_HEADERS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
export const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const isValidDate = (d) => d instanceof Date && !Number.isNaN(d.getTime());

const pad2 = (n) => String(n).padStart(2, '0');

export const formatMinutesOfDay = (minutes) => `${pad2(Math.floor(minutes / 60))}:${pad2(minutes % 60)}`;

// Monday-start weeks, matching the Term Planner's own day order
// (TermManagementPanel.js PLANNER_DAYS: Mon..Sun).
export const startOfWeek = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  return d;
};

export const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};

export const addWeeks = (date, weeks) => addDays(date, weeks * 7);

export const addMonths = (date, months) => {
  const d = new Date(date);
  const day = d.getDate();
  d.setDate(1);
  d.setMonth(d.getMonth() + months);
  // Clamp to the month's real length (31 Jan + 1 month is 28/29 Feb).
  const lastDay = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
  d.setDate(Math.min(day, lastDay));
  return d;
};

export const startOfMonth = (date) => {
  const d = new Date(date);
  d.setDate(1);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Local-calendar-day key ("2027-03-02") — the join key between occurrences
// and month cells. Deliberately local, never UTC: a 18:00 UTC booking must
// land on the day the organisation actually experiences it on.
export const localDayKey = (date) => `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;

export const minutesOfDay = (date) => date.getHours() * 60 + date.getMinutes();

// The visible week: [Monday 00:00, next Monday 00:00).
export const weekRangeOf = (anchor) => {
  const rangeStart = startOfWeek(anchor);
  return { rangeStart, rangeEndExclusive: addDays(rangeStart, 7) };
};

// The month grid always shows 6 full Mon-start weeks (42 cells), the Term
// Planner's dense-grid convention, so every month renders identically
// shaped and a fixed cell count is assertable.
export const monthGridRangeOf = (anchor) => {
  const rangeStart = startOfWeek(startOfMonth(anchor));
  return { rangeStart, rangeEndExclusive: addDays(rangeStart, 42) };
};

// — — — series range expansion (see module header) — — —

const ruleIntervalWeeks = (rrule) => {
  const match = /INTERVAL=(\d+)/i.exec(String(rrule || ''));
  const parsed = match ? parseInt(match[1], 10) : 1;
  return Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
};

// True when the rule keeps producing occurrences past `lastStart` even
// though the (cap-bounded) expansion stopped there: open-ended rules, or
// rules bounded only by UNTIL that has not passed yet. COUNT-bounded
// series are never re-phased (see module header).
const seriesContinuesPast = (expanded) => expanded.isOpenEnded
  || (!expanded.isTruncated && expanded.untilDate !== null
    && expanded.untilDate.getTime() >= Date.now());

const shiftWeeksPreservingPhase = (booking, weeks) => {
  const anchorStart = new Date(booking.start_time);
  const shiftedStart = new Date(anchorStart.getTime() + weeks * 7 * ruleIntervalWeeks(booking.google_recurrence_rule) * DAY_MS);
  const anchorEnd = booking.end_time ? new Date(booking.end_time) : null;
  const durationMs = anchorEnd ? anchorEnd.getTime() - anchorStart.getTime() : null;
  return {
    ...booking,
    start_time: shiftedStart.toISOString(),
    end_time: durationMs !== null ? new Date(shiftedStart.getTime() + durationMs).toISOString() : booking.end_time,
  };
};

// One series' buildSeriesEntry, viewport-adjusted to the requested range
// (see module header). Returns the entry's occurrence list unchanged in
// shape — including the per-date exception overlay buildSeriesEntry owns.
const seriesOccurrencesForRange = (seriesBookings, rangeStart, rangeEndExclusive) => {
  const withRule = seriesBookings.find((b) => b.google_recurrence_rule);
  if (!withRule) {
    return buildSeriesEntry(seriesBookings).occurrences;
  }
  const expanded = expandOccurrences(
    withRule.google_recurrence_rule,
    withRule.start_time,
    withRule.end_time,
  );
  const last = expanded.occurrences[expanded.occurrences.length - 1];
  let rows = seriesBookings;
  if (last && last.start.getTime() < rangeStart.getTime() && seriesContinuesPast(expanded)) {
    const periodMs = 7 * ruleIntervalWeeks(withRule.google_recurrence_rule) * DAY_MS;
    const weeks = Math.max(1, Math.floor((rangeStart.getTime() - new Date(withRule.start_time).getTime()) / periodMs));
    rows = seriesBookings.map((booking) => (booking === withRule
      ? shiftWeeksPreservingPhase(booking, weeks)
      : booking));
  }
  return buildSeriesEntry(rows).occurrences;
};

const requesterOf = (booking) => booking?.user_name
  || booking?.external_organizer_email
  || 'Unknown requester';

const toCalendarOccurrence = (occurrence, seriesPattern) => {
  const source = occurrence.sourceBooking || {};
  const start = occurrence.start;
  const end = isValidDate(occurrence.end) ? occurrence.end : null;
  return {
    key: `${source.id ?? 'series'}:${start.getTime()}`,
    bookingId: source.id ?? null,
    title: source.title || 'Booking',
    roomId: source.room_id ?? null,
    roomName: source.room_name || (source.room_id ? `Room ${source.room_id}` : 'Unassigned room'),
    requester: requesterOf(source),
    start,
    end,
    isException: Boolean(occurrence.isException),
    statusKey: occurrence.statusKey,
    statusText: occurrence.statusText,
    statusBadgeClass: occurrence.statusBadgeClass,
    seriesPattern: seriesPattern || '',
    sourceBooking: source,
  };
};

const occurrenceOverlapsRange = (occurrence, rangeStart, rangeEndExclusive) => {
  const end = isValidDate(occurrence.end) ? occurrence.end : new Date(occurrence.start.getTime() + 60 * 1000);
  return occurrence.start.getTime() < rangeEndExclusive.getTime()
    && end.getTime() > rangeStart.getTime();
};

// The flat occurrence list for any [rangeStart, rangeEndExclusive) window:
// singles whose real window overlaps the range, plus every series
// occurrence (rule-expanded, exception-overlaid) inside it. Sorted by
// start time.
export const expandBookingsForRange = (bookings, rangeStart, rangeEndExclusive) => {
  const list = Array.isArray(bookings) ? bookings : [];
  const seriesGroups = new Map();
  const singles = [];

  list.forEach((booking) => {
    const key = buildSeriesKey(booking);
    if (key) {
      if (!seriesGroups.has(key)) seriesGroups.set(key, []);
      seriesGroups.get(key).push(booking);
    } else {
      singles.push(booking);
    }
  });

  const occurrences = [];

  seriesGroups.forEach((seriesBookings, key) => {
    const representative = seriesBookings.find((b) => b.google_recurrence_rule)
      || [...seriesBookings].sort((a, b) => new Date(a.start_time) - new Date(b.start_time))[0];
    const pattern = describeSeriesPattern(
      representative.google_recurrence_rule,
      representative.start_time,
    );
    seriesOccurrencesForRange(seriesBookings, rangeStart, rangeEndExclusive)
      .filter((occurrence) => occurrenceOverlapsRange(occurrence, rangeStart, rangeEndExclusive))
      .forEach((occurrence) => {
        // M005-F0067 (WEB-BKHUB-022): every series occurrence carries the
        // SERIES MASTER's booking id (the representative — the rule-bearing
        // row, else the earliest row — the same row the hub cards already
        // use as card.id). A per-date exception row keeps its own id as
        // bookingId for detail/status, but its Edit-booking link must open
        // the series master — the row that owns the recurrence rule.
        occurrences.push({
          ...toCalendarOccurrence(occurrence, pattern),
          seriesKey: key,
          seriesBookingId: representative.id ?? null,
        });
      });
  });

  singles.forEach((booking) => {
    // Same decoration the hub cards use (decorateOccurrence's fields) so a
    // single booking carries exactly the same status vocabulary as series
    // occurrences do.
    const occurrence = {
      start: new Date(booking.start_time),
      end: booking.end_time ? new Date(booking.end_time) : null,
      sourceBooking: booking,
      isException: false,
      statusKey: resolveDisplayStatus(booking).key,
      statusText: getStatusText(booking),
      statusBadgeClass: getStatusBadgeClass(booking),
    };
    if (!isValidDate(occurrence.start)) return;
    if (!occurrenceOverlapsRange(occurrence, rangeStart, rangeEndExclusive)) return;
    occurrences.push({ ...toCalendarOccurrence(occurrence, ''), seriesKey: null, seriesBookingId: null });
  });

  return occurrences.sort((a, b) => a.start - b.start);
};

// — — — week view — — —

const DEFAULT_WINDOW = { startMin: 8 * 60, endMin: 22 * 60 };
const MIN_WINDOW_SPAN = 2 * 60;

// The day window all lanes share: derived from the week's real bookings
// (clamped to whole hours, never smaller than two hours), so an
// 18:00–23:00 studio does not render an empty 08:00–17:00 void.
export const deriveDayWindow = (occurrences) => {
  let min = Infinity;
  let max = -Infinity;
  occurrences.forEach((occurrence) => {
    const startMin = minutesOfDay(occurrence.start);
    const end = isValidDate(occurrence.end) && occurrence.end.getTime() > occurrence.start.getTime()
      ? occurrence.end
      : null;
    // A booking ending past midnight fills the rest of its start day.
    const endMin = end ? Math.max(minutesOfDay(end), startMin + 30) : startMin + 60;
    min = Math.min(min, startMin);
    max = Math.max(max, endMin);
  });
  if (!Number.isFinite(min) || !Number.isFinite(max)) return { ...DEFAULT_WINDOW };
  const startMin = Math.max(0, Math.floor(min / 60) * 60);
  const endMin = Math.min(24 * 60, Math.max(Math.ceil(max / 60) * 60, startMin + MIN_WINDOW_SPAN));
  return { startMin, endMin };
};

// Greedy column packing for overlapping blocks in one day cell: each block
// gets an equal-width column, left-to-right in start order.
export const layoutDayBlocks = (blocks) => {
  const columns = [];
  const placed = blocks.map((block) => {
    const startMin = minutesOfDay(block.occurrence.start);
    const end = isValidDate(block.occurrence.end) ? block.occurrence.end : block.occurrence.start;
    const endMinutes = end.getTime() > block.occurrence.start.getTime()
      ? minutesOfDay(end)
      : startMin + 30;
    const extent = { startMin, endMin: Math.max(endMinutes, startMin + 30), block };
    const freeColumn = columns.findIndex((columnEnd) => columnEnd <= extent.startMin);
    const columnIndex = freeColumn >= 0 ? freeColumn : columns.length;
    columns[columnIndex] = extent.endMin;
    return { ...extent, columnIndex };
  });
  const totalColumns = Math.max(1, columns.length);
  return placed.map(({ block, startMin, endMin, columnIndex }) => ({
    occurrence: block.occurrence,
    startMin,
    endMin,
    leftPct: (columnIndex / totalColumns) * 100,
    widthPct: 100 / totalColumns,
  }));
};

// Week view data: one lane per room (rooms the selected organisation has,
// via GET /api/v1/rooms?org=), each lane holding the visible week's
// occurrences placed by real time. An occurrence whose room the rooms read
// does not list (deleted since, or unassigned) still renders — in a
// trailing lane — rather than being silently dropped.
export const buildWeekView = ({ bookings, rooms, anchor }) => {
  const { rangeStart, rangeEndExclusive } = weekRangeOf(anchor);
  const occurrences = expandBookingsForRange(bookings, rangeStart, rangeEndExclusive);
  const days = Array.from({ length: 7 }, (_, index) => addDays(rangeStart, index));

  const laneList = (Array.isArray(rooms) ? rooms : [])
    .filter((room) => room && (room.id ?? room.room_id) !== undefined)
    .map((room) => ({
      key: `room:${room.id ?? room.room_id}`,
      label: room.name || `Room ${room.id ?? room.room_id}`,
      roomId: room.id ?? room.room_id,
    }))
    .sort((a, b) => String(a.label).localeCompare(String(b.label)));

  const seen = new Set(laneList.map((lane) => String(lane.roomId)));
  occurrences.forEach((occurrence) => {
    if (occurrence.roomId === null || seen.has(String(occurrence.roomId))) return;
    seen.add(String(occurrence.roomId));
    laneList.push({
      key: `room:${occurrence.roomId}`,
      label: occurrence.roomName,
      roomId: occurrence.roomId,
    });
  });
  laneList.sort((a, b) => String(a.label).localeCompare(String(b.label)));

  const window = deriveDayWindow(occurrences);
  const spanMin = Math.max(1, window.endMin - window.startMin);

  const lanes = laneList.map((lane) => ({
    ...lane,
    cells: days.map((day) => {
      const dayKey = localDayKey(day);
      const dayOccurrences = occurrences.filter((occurrence) => String(occurrence.roomId) === String(lane.roomId)
        && localDayKey(occurrence.start) === dayKey);
      const blocks = layoutDayBlocks(dayOccurrences.map((occurrence) => ({ occurrence }))).map((block) => {
        const startMin = minutesOfDay(block.occurrence.start);
        const rawEnd = isValidDate(block.occurrence.end) && block.occurrence.end.getTime() > block.occurrence.start.getTime()
          ? minutesOfDay(block.occurrence.end)
          : startMin + 30;
        const topPct = ((Math.max(startMin, window.startMin) - window.startMin) / spanMin) * 100;
        const endPct = ((Math.min(rawEnd, window.endMin) - window.startMin) / spanMin) * 100;
        return {
          occurrence: block.occurrence,
          startMin,
          endMin: rawEnd,
          leftPct: block.leftPct,
          widthPct: block.widthPct,
          topPct: Math.max(0, topPct),
          heightPct: Math.min(100 - Math.max(0, topPct), Math.max(6, endPct - topPct)),
        };
      });
      return { day, dayKey, blocks };
    }),
  }));

  return {
    days,
    window,
    lanes,
    rangeLabel: `${days[0].getDate()} ${MONTH_NAMES[days[0].getMonth()]} – ${days[6].getDate()} ${MONTH_NAMES[days[6].getMonth()]} ${days[6].getFullYear()}`,
  };
};

// — — — month view — — —

// Status dots reuse the app's actual status-colour constants — the
// `status-badge` modifier classes the ported bookingStatus.js already
// emits for every card badge — by taking the modifier token straight off
// each occurrence's real statusBadgeClass ('status-badge success', …). No
// second colour vocabulary is defined anywhere in this feature (§8:
// match the existing status colours, never invent new ones).
export const statusClassOf = (occurrence) => {
  const tokens = String(occurrence.statusBadgeClass || '').split(/\s+/);
  return tokens.find((token) => token && token !== 'status-badge') || '';
};

const STATUS_ORDER = ['approved', 'pending', 'rejected', 'delete_pending', 'cancelled'];

// Month view data: every grid cell with its day's occurrence count and the
// distinct statuses present (count + status colour per §8), plus the
// occurrences themselves so a clicked cell can render that day's list.
export const buildMonthView = ({ bookings, anchor }) => {
  const { rangeStart, rangeEndExclusive } = monthGridRangeOf(anchor);
  const occurrences = expandBookingsForRange(bookings, rangeStart, rangeEndExclusive);
  const monthStartDate = startOfMonth(anchor);

  const cells = Array.from({ length: 42 }, (_, index) => {
    const day = addDays(rangeStart, index);
    const dayKey = localDayKey(day);
    const dayOccurrences = occurrences.filter((occurrence) => localDayKey(occurrence.start) === dayKey);

    const statusCounts = new Map();
    dayOccurrences.forEach((occurrence) => {
      const statusClass = statusClassOf(occurrence);
      const current = statusCounts.get(statusClass) || { count: 0, statusText: occurrence.statusText };
      statusCounts.set(statusClass, { ...current, count: current.count + 1 });
    });
    const statuses = [...statusCounts.entries()]
      .map(([statusClass, { count, statusText }]) => ({ statusClass, count, statusText }))
      .sort((a, b) => {
        const aIndex = STATUS_ORDER.indexOf(a.statusClass);
        const bIndex = STATUS_ORDER.indexOf(b.statusClass);
        return (aIndex === -1 ? STATUS_ORDER.length : aIndex) - (bIndex === -1 ? STATUS_ORDER.length : bIndex)
          || a.statusClass.localeCompare(b.statusClass);
      });

    return {
      day,
      dayKey,
      inMonth: day.getMonth() === monthStartDate.getMonth(),
      isToday: localDayKey(new Date()) === dayKey,
      count: dayOccurrences.length,
      statuses,
      occurrences: dayOccurrences,
    };
  });

  return {
    cells,
    rangeLabel: `${MONTH_NAMES[monthStartDate.getMonth()]} ${monthStartDate.getFullYear()}`,
    weekdayHeaders: WEEKDAY_HEADERS,
  };
};
